import { differenceInCalendarDays } from 'date-fns'
import { useCallback, useEffect, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useParams } from 'react-router'
import { AcfuProgressiveDisclosureParameters, AppointmentType, EventType, RouteParameters } from '../../constants'
import { AppointmentFormData } from '../../types'
import { isAcfuProgressiveDisclosureEligible } from '../../util'
import { useAcfuActingUser } from '../useAcfuActingUser'
import { useCountry } from '../useCountry'
import { useEventTracking } from '../useEventTracking'
import { useFeatureFlags } from '../useFeatureFlags'
import { useNow } from '../useNow'
import { useRescheduleContext } from '../useRescheduleContext'

interface UseAcfuProgressiveDisclosure {
  canShowMoreDates: boolean
  isAcfuEligible: boolean
  onShowMoreDates: () => void
  requiresExtendedWindowReason: boolean
  windowInDays: number
}

// PX-3649: single source of truth for ACFU-progressive-disclosure eligibility, the current
// window size, the "Show more dates" action, and whether the currently selected date requires a
// reason — reused by every calendar/window call site and both the /consultant and /schedule
// booking flows, instead of each one re-deriving eligibility and re-implementing the
// expand-window/track/setValue sequence independently.
const useAcfuProgressiveDisclosure = (fallbackWindowInDays: number): UseAcfuProgressiveDisclosure => {
  const { getValues, setValue } = useFormContext<AppointmentFormData>()
  const [type, journey, acfuVisibleWindowDays, clientStartDate] = useWatch<AppointmentFormData>({
    name: ['type', 'journey', 'acfuVisibleWindowDays', 'clientStartDate'],
  })
  const country = useCountry()
  const [isAcfuProgressiveDisclosureEnabled] = useFeatureFlags(['ACFU_PROGRESSIVE_DATE_DISCLOSURE'])
  const { track } = useEventTracking()
  const { actingUserId, actingUserRole } = useAcfuActingUser()
  const { data: now = new Date() } = useNow()

  // Two different reschedule signals exist depending on which booking flow rendered this hook —
  // both are safe to read unconditionally (useParams() and useRescheduleContext() each have a
  // safe empty/default fallback outside their matching route/provider), so reading both and
  // OR-ing them covers the /consultant (route-param-based) and /schedule (context-based) flows
  // without either call site needing to know which signal applies to it.
  const { [RouteParameters.APPOINTMENT_ID]: appointmentId } = useParams()
  const rescheduleContext = useRescheduleContext()
  const isRescheduling = Boolean(appointmentId) || Boolean(rescheduleContext?.reason)

  const isAcfuEligible = useMemo(
    () =>
      isAcfuProgressiveDisclosureEligible({
        appointmentType: type as AppointmentType,
        country,
        isFlagEnabled: isAcfuProgressiveDisclosureEnabled as boolean,
        isRescheduling,
        journey: journey as string,
      }),
    [country, isAcfuProgressiveDisclosureEnabled, isRescheduling, journey, type]
  )

  const windowInDays = isAcfuEligible ? (acfuVisibleWindowDays as number) : fallbackWindowInDays
  const canShowMoreDates = isAcfuEligible && windowInDays < AcfuProgressiveDisclosureParameters.MAX_WINDOW_IN_DAYS

  // Whether a reason is required is driven by the actually selected date, not by whether the
  // window was ever expanded — someone who clicks "Show more dates" and then picks a date still
  // within the 3-day default shouldn't be asked for a reason. Written into the form (rather than
  // just returned) so the two validationSchema.ts files — which can't call a hook — can read it.
  const requiresExtendedWindowReason = Boolean(
    isAcfuEligible &&
      clientStartDate &&
      differenceInCalendarDays(clientStartDate as Date, now) > AcfuProgressiveDisclosureParameters.DEFAULT_WINDOW_IN_DAYS
  )

  useEffect(() => {
    setValue('acfuRequiresExtendedWindowReason', requiresExtendedWindowReason)
  }, [requiresExtendedWindowReason, setValue])

  const onShowMoreDates = useCallback(() => {
    const expandedToDays = Math.min(
      windowInDays + AcfuProgressiveDisclosureParameters.EXPANSION_STEP_IN_DAYS,
      AcfuProgressiveDisclosureParameters.MAX_WINDOW_IN_DAYS
    )

    track(EventType.ACFU_MORE_DATES_CLICKED, {
      data: {
        actingUserRole,
        currentWindowDays: windowInDays,
        expandedToDays,
        userId: actingUserId,
      },
    })

    setValue('acfuShowMoreClickCount', (Number(getValues('acfuShowMoreClickCount')) || 0) + 1)
    setValue('acfuVisibleWindowDays', expandedToDays)
  }, [actingUserId, actingUserRole, getValues, setValue, track, windowInDays])

  return { canShowMoreDates, isAcfuEligible, onShowMoreDates, requiresExtendedWindowReason, windowInDays }
}

export { useAcfuProgressiveDisclosure }
